import React, { PropsWithChildren, useId, useState } from "react";
import styles from "./styles.module.scss";

/**
 * FAQ Item组件 - 处理单个问题的展开和折叠
 */
function Item({ header, children, defaultOpen = false }: PropsWithChildren<{ header: string; defaultOpen?: boolean }>) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [hasFocus, setHasFocus] = useState(false);
  const id = useId();

  return (
    <section className="faq-section">
      <h3>
        <button
          id={id + "-button"}
          aria-expanded={isOpen}
          aria-controls={id}
          onClick={() => setIsOpen(!isOpen)}
          onFocus={e => {
            if (e.type === "focus" && e.target.matches(":focus-visible")) {
              setHasFocus(true);
            }
          }}
          onBlur={() => setHasFocus(false)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flex: 1,
            position: "relative",
            background: "none",
            border: "none",
            color: "white",
            fontSize: "18px",
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "left",
            padding: 0,
            outline: "none",
            width: "100%",
          }}>
          <span style={{ textAlign: "left", position: "relative", zIndex: 1 }}>{header}</span>
          <ChevronDownIcon isOpen={isOpen} />
          {hasFocus && (
            <div
              style={{
                position: "absolute",
                inset: "-8px",
                background: "rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                zIndex: 0,
              }}
            />
          )}
        </button>
      </h3>
      <div
        id={id}
        aria-labelledby={id + "-button"}
        style={{
          height: isOpen ? "auto" : 0,
          overflow: "hidden",
          transition: "height 0.3s ease",
        }}>
        <div style={{ padding: "20px 0 0", opacity: isOpen ? 1 : 0, transition: "opacity 0.3s ease" }}>{children}</div>
      </div>
      <hr />
    </section>
  );
}

/**
 * FAQ组件 - 包含多个问答条目
 */
export default function FAQ() {
  return (
    <div className={styles.container}>
      <div className={styles.faqWrapper}>
        {/* FAQ Header */}
        <div className={styles.faqHeader}>
          <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
          <p className={styles.faqSubtitle}>Everything you need to know about Fellou</p>
        </div>

        {/* FAQ Accordion */}
        <div className={styles.faqAccordion}>
          <Item header="How is an AI browser different from a chat assistant?" defaultOpen={true}>
            <p className={styles.faqAccordion1}>
              An AI chat assistant is reactive, only answering your questions. An AI Browser with agentic AI can
              automatically run redundant work for you and even proactively help you to complete tasks you've forgotten.
            </p>
            <p className={styles.faqAccordion1}>
              <strong className={styles.faqAccordionItem}>The biggest differences:</strong>
            </p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                <strong className={styles.faqAccordionItem}>Deep action:</strong> Fellou independently plan and execute
                complex web and desktop based tasks across multiple apps to achieve your goals.
              </li>
              <li className={styles.faqAccordionLi}>
                <strong className={styles.faqAccordionItem}>Deep search:</strong> Fellou automates in-depth research
                across the entire internet, including logged-in accounts on platforms like X, Reddit or Salefore.
              </li>
              <li className={styles.faqAccordionLi}>
                <strong className={styles.faqAccordionItem}>Dynamic Multitasking:</strong> Fellou runs multiple tasks
                simultaneously in its back-end workspace, while users browse other sites.
              </li>
              <li className={styles.faqAccordionLi}>
                <strong className={styles.faqAccordionItem}>Agentic memory:</strong> Fellou can offer proactive,
                personalized help by connecting browser history, chat history, contextual page data, and personal
                knowledge base.
              </li>
            </ul>
          </Item>

          <Item header="Can AI browsers do more than just summarize pages and organize tabs?">
            <p className={styles.faqAccordion1}>
              Yes, it goes far beyond basic summarization. An Agentic AI Browser automates complex work across the web.
            </p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>Conducts in-depth research to generate reports.</li>
              <li className={styles.faqAccordionLi}>Automates data entry and social media management.</li>
              <li className={styles.faqAccordionLi}>
                Automatically executes custom, multi-step workflows you define to achieve specific goals.
              </li>
            </ul>
          </Item>

          <Item header="Can AI automate my work across different apps and websites?">
            <p className={styles.faqAccordion1}>
              Yes, cross-platform automation is a core function of an Agentic Browser.
            </p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                It executes complex workflows across different apps and websites.
              </li>
              <li className={styles.faqAccordionLi}>
                It can pull data from one platform (like LinkedIn) and use it in another (like Notion).
              </li>
              <li className={styles.faqAccordionLi}>
                This provides a more integrated automation experience than API-based tools.
              </li>
            </ul>
          </Item>

          <Item header="Does it work on sites that require a login or CAPTCHA?">
            <p className={styles.faqAccordion1}>Yes, a key capability of our AI Browser is navigating the real web.</p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                It is designed to handle logins, authentication, and even CAPTCHA challenges.
              </li>
              <li className={styles.faqAccordionLi}>
                Advanced AI can solve CAPTCHAs with high accuracy by simulating human behavior.
              </li>
              <li className={styles.faqAccordionLi}>This enables automation on secure sites.</li>
            </ul>
          </Item>

          <Item header="Can I see and control exactly what the AI agent is doing?">
            <p className={styles.faqAccordion1}>
              Yes, our AI Browser is designed for complete transparency and user control.
            </p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                Visually inspect and edit the AI Browser's automatic workflow before it runs.
              </li>
              <li className={styles.faqAccordionLi}>Monitor the agent's progress in real-time.</li>
              <li className={styles.faqAccordionLi}>
                Intervene at any step to adjust or halt the task, ensuring you always have full oversight.
              </li>
            </ul>
          </Item>

          <Item header="What can AI Browser create other than answering questions by text?">
            <p className={styles.faqAccordion1}>Yes, it's a multi-modal creation tool. Our AI Browser can generate:</p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                In-depth research reports that include text, images, and charts.
              </li>
              <li className={styles.faqAccordionLi}>Creative content such as images, logos, SVGs, and even music.</li>
              <li className={styles.faqAccordionLi}>
                Structured documents like presentations (PPTs) and spreadsheets (Excel).
              </li>
              <li className={styles.faqAccordionLi}>
                Code for web pages and applications&mdash;from websites and games to interactive models&mdash;using
                HTML, CSS, and JavaScript.
              </li>
            </ul>
          </Item>

          <Item header="Can I schedule tasks to run automatically in the future?">
            <p className={styles.faqAccordion1}>Yes, you can schedule any complex workflow to run automatically.</p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                Go to Fellou home and find tasks. You will see scheduled settings
              </li>
              <li className={styles.faqAccordionLi}>
                Click +New Schedule to set tasks to execute at a specific time or on a recurring basis.
              </li>
              <li className={styles.faqAccordionLi}>
                This goes beyond simple reminders, enabling full delegation of routine digital work. It's ideal for
                recurring reports or data monitoring.
              </li>
            </ul>
          </Item>

          <Item header="Can I build my own custom AI agents and workflows?">
            <p className={styles.faqAccordion1}>Yes, Fellou is an extensible platform for developers.</p>
            <ul className={styles.faqAccordionUl}>
              <li className={styles.faqAccordionLi}>
                Use our open-source Eko 2.0 framework to build custom AI agents.
              </li>
              <li className={styles.faqAccordionLi}>
                Create powerful, intervenable agents that run within the Fellou AI Browser by using a hybrid of natural
                language and JavaScript.
              </li>
              <li className={styles.faqAccordionLi}>Tailor automation to your specific needs.</li>
            </ul>
          </Item>
        </div>

        {/* 样式组件 - 用于处理最后一个section的分隔线隐藏 */}
        <StyleSheet />
      </div>
    </div>
  );
}

/**
 * 样式组件 - 提供FAQ所需的基础样式
 */
function StyleSheet() {
  return (
    <style>{`
      .faq-section {
        padding: 24px;
        position: relative;
      }
      
      .faq-section h3 {
        margin: 0;
        display: flex;
      }
      
      .faq-section hr {
        margin: 0;
        border: 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        position: absolute;
        bottom: 0;
        left: 24px;
        right: 24px;
        z-index: 0;
      }
      
      .faq-section:last-child hr {
        display: none;
      }
    `}</style>
  );
}

/**
 * 下拉箭头图标组件
 */
function ChevronDownIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        color: "rgba(255, 255, 255, 0.6)",
        flexShrink: 0,
        transform: isOpen ? "rotate(180deg)" : "rotate(0)",
        transition: "transform 0.3s ease",
      }}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
